import React, { useState, useMemo, useCallback, useEffect, memo } from "react";
import CardTable from "../../../components/common/CardTable";
import Table, { TableColumn, TableRow } from "../../../components/common/Table/Table";
import Pagination from "../../../components/common/Table/Pagination";

const COSTCENTER_COLUMN_DEFINITIONS: TableColumn[] = [
  { key: 'glString', label: 'GL String' },
  { key: 'entity', label: 'Entity' },
  { key: 'location', label: 'Location' },
  { key: 'costCenter', label: 'Cost Center' },
  { key: 'shift', label: 'Shift' },
  { key: 'noOfEmployees', label: '# Employees' },
  { key: 'editAction', label: 'Edit' },
  { key: 'deleteAction', label: 'Delete' }
];

const ITEMS_PER_PAGE = 5;

interface CostCenterListProps {
  showUBTCostCenterList: boolean;
  costCenterListLength: number;
  costCenterRow: TableRow[];
  onActionClick: (rowData: TableRow) => void;
}

/**
 * Renders the paginated cost center table for the active UBT profile.
 * Column headers for Edit/Delete are always shown; individual row buttons
 * are rendered as null when the user lacks edit privileges.
 * @param props - The table visibility, row data, and action handlers.
 * @returns The cost center list UI when rows are available.
 */
const CostCenterList: React.FC<CostCenterListProps> = ({
  showUBTCostCenterList,
  costCenterListLength,
  costCenterRow,
  onActionClick,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage((previousPage) => {
      if (!showUBTCostCenterList || costCenterListLength === 0) {
        return 1;
      }

      const lastPage = Math.max(1, Math.ceil(costCenterListLength / ITEMS_PER_PAGE));
      // Keep current page when it's still within range; otherwise clamp to last page
      return Math.min(previousPage, lastPage);
    });
  }, [showUBTCostCenterList, costCenterListLength]);

  // Calculate paginated rows
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return costCenterRow.slice(startIndex, endIndex);
  }, [costCenterRow, currentPage]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  if (!showUBTCostCenterList || costCenterListLength === 0) {
    return null;
  }

  return (
    <div className="mt-10!">
      <CardTable>
        {costCenterRow.length > 0 ? (
          <>
            <Table
              columns={COSTCENTER_COLUMN_DEFINITIONS}
              rows={paginatedRows}
              actionKey="ubtName"
              onActionClick={onActionClick}
            />
            <div className="mt-4 mobile:relative mobile:left-7.5">
              <Pagination
                totalItems={costCenterListLength}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        ) : (
          <p className="p3 -book text-center mt-2.5! desktop:mt-50!">
            No Cost Center found
          </p>
        )}
      </CardTable>
    </div>
  );
};

export default memo(CostCenterList);
